import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API = `${API_BASE_URL}/api/dsc`;

const LOCAL_DSC = (process.env.REACT_APP_DSC_LOCAL_URL || 'http://127.0.0.1:16443').replace(/\/$/, '');

let isMockModeCache = null;

const checkMockMode = async () => {
    if (isMockModeCache !== null) return isMockModeCache;

    try {
        const res = await axios.get(`${API}/status`);
        isMockModeCache = res?.data?.mode === 'mock' || res?.data?.isMock === true;
        console.log(`🔐 DSC Mode: ${isMockModeCache ? 'MOCK' : 'PRODUCTION'}`);
        return isMockModeCache;
    } catch (error) {
        console.warn('Failed to check DSC mode, defaulting to mock');
        isMockModeCache = true;
        return true;
    }
};

const checkLocalHelper = async () => {
    try {
        const res = await axios.get(`${LOCAL_DSC}/health`, {
            timeout: 3000
        });

        const data = res?.data || {};
        const running = res.status === 200 && data.success === true && data.status === 'running';

        return {
            success: running,
            reachable: true,
            data
        };
    } catch (error) {
        console.error('❌ Local DSC helper health check failed:', error?.message);
        return {
            success: false,
            reachable: false,
            error
        };
    }
};

const prepareDSC = async (data) => {
    console.log('📡 prepareDSC called');
    const res = await axios.post(`${API}/prepare`, { data });
    return res.data;
};

const signViaLocalClient = async ({ payloadBase64, hashBase64, algorithm, pin }) => {
    const isMock = await checkMockMode();

    if (isMock) {
        console.log('🔐 MOCK MODE: Generating fake signature');
        const fakeSignature = 'mock_' + Date.now() + '_' + Math.random().toString(36).substring(7);
        return {
            success: true,
            signature: fakeSignature,
            certificatePem: '-----BEGIN CERTIFICATE-----\nMOCK_CERTIFICATE\n-----END CERTIFICATE-----',
            mode: 'mock'
        };
    }

    const helperStatus = await checkLocalHelper();
    if (!helperStatus.success) {
        return {
            success: false,
            code: 'HELPER_NOT_RUNNING',
            message: 'DSC Helper is not running. Please start the DSC Helper app and try again.'
        };
    }

    try {
        console.log('🔐 PRODUCTION MODE: Calling local helper');
        const res = await axios.post(
            `${LOCAL_DSC}/sign`,
            {
                payloadBase64,
                hashBase64,
                algorithm,
                pin
            },
            {
                timeout: 60000,
                headers: {
                    'Content-Type': 'application/json'
                }
            }
        );

        const data = res?.data || {};

        if (!data.success) {
            return {
                success: false,
                code: data.code || 'SIGN_FAILED',
                message: data.message || 'DSC signing failed on local helper.'
            };
        }

        return data;
    } catch (err) {
        console.error('Helper sign error:', err);

        if (err.response?.data) {
            return {
                success: false,
                code: err.response.data.code || 'HELPER_ERROR',
                message: err.response.data.message || 'DSC Helper returned an error.'
            };
        }

        if (err.code === 'ECONNABORTED') {
            return {
                success: false,
                code: 'HELPER_TIMEOUT',
                message: 'DSC Helper took too long to respond. Please try again.'
            };
        }

        return {
            success: false,
            code: 'HELPER_CONNECTION_ERROR',
            message: 'Unable to connect to DSC Helper. Please make sure it is running on this computer.'
        };
    }
};

const verifyDSCSignature = async ({ data, payloadBase64, signature, certificatePem, algorithm }) => {
    const isMock = await checkMockMode();

    if (isMock) {
        console.log('🔐 MOCK MODE: Skipping verification');
        return {
            success: true,
            verified: true,
            mode: 'mock',
            message: 'Mock verification successful'
        };
    }

    try {
        // Get user info from sessionStorage to send as headers
        const userStr = sessionStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : {};
        const token = sessionStorage.getItem('token');

        const res = await axios.post(`${API}/sign`, {
            data,
            payloadBase64,
            signature,
            certificatePem,
            algorithm
        }, {
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                'x-user-id': user.id,
                'x-user-name': user.name || user.username
            }
        });

        return res.data;
    } catch (err) {
        console.error('verifyDSCSignature error:', err?.response?.data || err);

        if (err.response?.data) {
            return err.response.data;
        }

        return {
            success: false,
            error: 'VERIFY_REQUEST_FAILED',
            message: err.message || 'Verification request failed'
        };
    }
};

export default {
    prepareDSC,
    signViaLocalClient,
    verifyDSCSignature,
    checkLocalHelper,
    checkMockMode
};